'use client';

import * as React from 'react';
import { usePathname } from 'next/navigation';
import type { CityCode } from '@loci/city-editions';
import { CityCoordStrip } from '@/components/molecules/CityCoordStrip';
import {
  CityHeader,
  type CityHeaderActiveNav,
  type CityHeaderProps,
} from '@/components/molecules/CityHeader';
import { cn } from '@/components/atoms/primitives/utils';
import { HostTransitionMount } from '@/components/organisms/AppShell/HostTransitionMount';
import { GlobalSearchShortcutMount } from '@/components/organisms/AppShell/GlobalSearchShortcutMount';
import { NotificationCenterMount } from '@/components/organisms/AppShell/NotificationCenterMount';
import { useCitySignInHref } from '@/hooks/useCitySignInHref';
import { useCitySignUpHref } from '@/hooks/useCitySignUpHref';
import { useCityAuthState } from '@/hooks/useCityAuthState';
import type { CityAuthUser } from '@/lib/city-nav/cityAuthUser';
import { useCityContext, useCityIsStaging } from '@/lib/city-context.client';
import { cityAppEntryUrl, defaultCityCode } from '@/lib/cross-host-urls';

export interface CityChromeViewProps {
  /** City display name, e.g. "Charlotte". */
  cityName: string;
  /** Latitude in decimal degrees. */
  lat: number;
  /** Longitude in decimal degrees. */
  lng: number;
  /** Live event count surfaced in the coord strip. Omit to hide the pulse cluster. */
  liveCount?: number;
  /** Which primary nav item is the current page (or `null` for none). */
  activeNav: CityHeaderActiveNav;
  /**
   * City code driving the coord-strip eyebrow + Wayfinder hrefs. The connected
   * wrapper derives it from `useCityContext()`; stories may set it explicitly.
   */
  cityCode?: CityCode;
  /** Optional path to the city lockup SVG. */
  lockupSrc?: CityHeaderProps['lockupSrc'];
  /** Optional override for the "Open app" CTA destination. */
  openAppUrl?: CityHeaderProps['openAppUrl'];
  /**
   * Optional override for the account-affordance signup destination.
   * When omitted, the connected wrapper builds it from request-host staging
   * (WEB-776) so a missing `NEXT_PUBLIC_LOCI_STAGE` cannot pin prod hosts.
   */
  signUpUrl?: CityHeaderProps['signUpUrl'];
  /** Optional override for the "Sign in" CTA destination. */
  signInUrl?: CityHeaderProps['signInUrl'];
  /**
   * Signed-in identity for the DES-60 auth swap (WEB-640). Pure view — the
   * connected {@link CityChrome} resolves this from SSR + `/auth/me`.
   */
  authUser?: CityAuthUser | null;
  /**
   * Cookie/hint-armed probe still settling with no identity yet (WEB-640).
   * Pure view — connected wrapper derives this from RTK fetch state.
   */
  isAuthPending?: boolean;
  /**
   * Request-host staging flag (WEB-776). Threaded into the authed account
   * profile href. Anon signup / sign-in / open-app URLs are resolved on the
   * connected wrapper.
   */
  isStaging?: boolean;
  /** Additional CSS classes applied to the outer wrapper. */
  className?: string;
}

/**
 * CityChromeView — pure presentational composition of `CityHeader` +
 * `CityCoordStrip` for the marketing-city surface (`<city>.loci.city`).
 *
 * The outer wrapper is `sticky top-0` with `--z-chrome` so the 82px
 * strip+bar block pins as one unit. FRE-1362 / DES-84 retired the scroll
 * solidify state — the bar is always-on glass at every scroll position, so
 * this view has no scroll-derived prop to keep deterministic.
 *
 * Storybook and unit tests target this view directly so the rendered output
 * is deterministic. Production mounts go through {@link CityChrome}, which
 * derives `activeNav` from the current pathname.
 */
export function CityChromeView({
  cityName,
  lat,
  lng,
  liveCount,
  activeNav,
  cityCode = defaultCityCode(),
  lockupSrc,
  openAppUrl,
  signUpUrl,
  signInUrl,
  authUser = null,
  isAuthPending = false,
  isStaging,
  className,
}: CityChromeViewProps): React.ReactElement {
  const authState = authUser ? 'authed' : isAuthPending ? 'pending' : 'anon';
  return (
    <div
      className={cn(
        // Sticky over fixed: document keeps its height; no content sits under
        // the chrome on load (CLS-safe). `--z-chrome` from WEB-611.
        'city-chrome sticky top-0 z-chrome flex w-full flex-col',
        className,
      )}
      data-testid="city-chrome"
      data-auth={authState}
    >
      {/* DS o-globalnav order: the 26px coord/stats strip sits ABOVE the 56px
          bar (continuity device on top). Previously rendered below the header,
          which read as the "stats rail in the wrong order". Dark coord ground
          stays `--n-950`; light uses `--bg-sunken` (FRE-1356). */}
      <CityCoordStrip
        cityName={cityName}
        lat={lat}
        lng={lng}
        liveCount={liveCount}
        cityCode={cityCode}
        // WEB-824: MUST be threaded. This whole subtree is `'use client'`, so
        // the strip renders on the server and again on hydration; letting it
        // resolve staging itself makes it read `window.location` on the client
        // only, and the resulting eyebrow text mismatch throws React #418.
        isStaging={isStaging}
      />
      <CityHeader
        cityName={cityName}
        activeNav={activeNav}
        cityCode={cityCode}
        lockupSrc={lockupSrc}
        openAppUrl={openAppUrl}
        signUpUrl={signUpUrl}
        signInUrl={signInUrl}
        authUser={authUser}
        isAuthPending={isAuthPending}
        isStaging={isStaging}
      />
    </div>
  );
}

export type CityChromeProps = Omit<
  CityChromeViewProps,
  'activeNav' | 'authUser' | 'isAuthPending'
> & {
  /**
   * Optional manual override for the active nav slot. When omitted, the
   * connected wrapper derives it from `usePathname()`. Useful for stories
   * and edge-case routes that should not mark a hub as active.
   */
  activeNav?: CityHeaderActiveNav;
  /**
   * SSR-resolved identity from {@link resolveCityAuthUser}. Seeds the first
   * paint so a signed-in visitor never flashes the anon CTA (WEB-640). When
   * omitted, the connected wrapper falls back to cookie presence / the
   * host-scoped session hint + `/auth/me`.
   */
  initialAuthUser?: CityAuthUser | null;
  /**
   * Server-observed `accessToken` / `refreshToken` cookie presence. True even
   * when the SSR `/auth/me` probe flapped — forces the client probe so a
   * cross-host session on `.loci.city` is not stranded by a missing
   * host-scoped localStorage hint (WEB-640 secondary trap).
   */
  hasAuthCookie?: boolean;
};

/**
 * Map the current pathname to a `CityHeader` `activeNav` slot. Returns `null`
 * for any path that does not start with one of the four hub prefixes so the
 * header reflects "no current page" cleanly on splash, error, and one-off
 * routes.
 *
 * The match is exact-or-prefix (e.g. `/category/music` activates `categories`).
 * `/categories` is not an alias: it never existed in the sitemap and now 301s
 * to `/` (WEB-1180 / DES-108).
 */
export function activeNavFromPathname(pathname: string | null): CityHeaderActiveNav {
  if (!pathname) return null;
  if (pathname === '/discover' || pathname.startsWith('/discover/')) return 'discover';
  if (pathname === '/neighborhoods' || pathname.startsWith('/neighborhoods/')) {
    return 'neighborhoods';
  }
  if (pathname === '/this-week' || pathname.startsWith('/this-week/')) return 'this-week';
  if (pathname === '/category' || pathname.startsWith('/category/')) {
    return 'categories';
  }
  return null;
}

/**
 * CityChrome — connected wrapper mounted once in `(city)/layout.tsx` (and
 * inside `CityHomeTemplate` on the city home).
 *
 * Reads `usePathname()` to decide which hub link is the current page, then
 * delegates to {@link CityChromeView}. Pass `activeNav` explicitly to opt out
 * of pathname inference; an explicit `null` is honored as "no active nav"
 * (using `??` would have collapsed `null` and `undefined` together).
 *
 * WEB-619: owns the 24px scroll sentinel + IntersectionObserver so the pure
 * view stays deterministic for Storybook / unit tests.
 *
 * WEB-640 / DES-60: resolves the auth-block swap. SSR seeds `initialAuthUser`
 * from the shared `/auth/me` probe (cookie-visible on `.loci.city`). The host-
 * scoped `localStorage` session hint alone is not enough — a session minted on
 * `app.loci.city` leaves no hint on `clt`. When SSR or the client probe
 * confirms a user, write the hint so later client navigations keep working.
 */
export function CityChrome({
  activeNav,
  cityCode,
  initialAuthUser = null,
  hasAuthCookie = false,
  ...rest
}: CityChromeProps): React.ReactElement {
  const pathname = usePathname();
  const city = useCityContext();
  const isStaging = useCityIsStaging();
  const resolved = activeNav !== undefined ? activeNav : activeNavFromPathname(pathname);
  // WEB-333: the coord-strip eyebrow + Wayfinder need the city code; the
  // layout only passes display name + coords, so derive it from the city
  // context (falling back to the registry default) unless overridden.
  const resolvedCityCode = cityCode ?? city?.code ?? defaultCityCode();
  // WEB-776: host-derived staging from `x-is-staging` (root layout provider).
  // Explicit overrides on the props still win. Env / window remain the
  // fallback when the provider is absent (stories / unit tests).
  const stage = { isStaging };
  const resolvedOpenAppUrl = rest.openAppUrl ?? cityAppEntryUrl(resolvedCityCode, stage);
  // FRE-1389: `next=<this page>` so signing up from the chooser / sitemap
  // returns the visitor here (the twin of the WEB-898 sign-in default).
  const resolvedSignUpUrl = useCitySignUpHref(resolvedCityCode, rest.signUpUrl, stage);
  const resolvedSignInUrl = useCitySignInHref(resolvedCityCode, rest.signInUrl, stage);

  // WEB-640 / DES-60: one shared resolution (SSR seed → `/auth/me` probe),
  // also read by the page-body conversion CTAs (WEB-1171). This mount owns the
  // host-scoped session-hint sync.
  const { authUser, isAuthPending } = useCityAuthState({
    initialAuthUser,
    hasAuthCookie,
    syncSessionHint: true,
  });

  return (
    <>
      {/* WEB-473: the DS 1.4.0 LayerTransition enter animation — plays once on
          arrival from a different LOCI host (Apex/App → City). Client-only;
          renders nothing unless a cross-host switch is detected (SSR-safe,
          reduced-motion-aware). Lives on the connected wrapper so stories /
          tests targeting the pure `CityChromeView` stay deterministic. */}
      <HostTransitionMount currentLayer="city" cityCode={resolvedCityCode} />
      {/* WEB-620: CityHeader advertises ⌘K / `/` but the city surface never
          mounted the listener (SiteHeader / AppShell do). Same idempotent
          `useSearchSheet` store as every other entry point. */}
      <GlobalSearchShortcutMount />
      {/* WEB-635: NotificationPanel host for the city menu Notifications row.
          Returns null while signed out; lights up with WEB-640's isAuthed gate. */}
      <NotificationCenterMount cityCode={resolvedCityCode} />
      <CityChromeView
        {...rest}
        cityCode={resolvedCityCode}
        activeNav={resolved}
        authUser={authUser}
        isAuthPending={isAuthPending}
        openAppUrl={resolvedOpenAppUrl}
        signUpUrl={resolvedSignUpUrl}
        signInUrl={resolvedSignInUrl}
        isStaging={isStaging}
      />
      {/* WEB-615 / DES-60: city host never mounts a bottom dock. Nav model
          follows surface — top chrome + menu for every visitor. The former
          FRE-874 `CityBottomNav` caused F-03 (dual nav at 768–1023), F-02
          (app furniture on a marketing surface), and F-01 (launcher collision). */}
    </>
  );
}
